import { useMemo } from 'react';

import { CAST_PILOT_ASSISTANT } from '@cast/constants';
import {
  Alert,
  Button,
  Icons,
  Tooltip,
  Typography,
  Stack,
} from '@cast/design-system';

import { Link } from 'components/router';
import { notReachable } from 'utils/types/not-reachable';

import { checkBinPackingBlock } from '../_utils/common';
import { BinPackingBlockedDetails, NodeWithDependencies } from '../types';
import { makeNodeDetailsLink } from '../utils/link';

import { useCluster } from '@cast/shared-cluster-context-data-access';

const NodeProblems = ({
  nodeProblems,
}: {
  nodeProblems: NonNullable<NodeWithDependencies['problems']>;
}) => {
  const hasMoreThanOneProblem = nodeProblems.length > 1;

  if (hasMoreThanOneProblem) {
    return (
      <Stack
        component="ul"
        data-testid="node-problems-section"
        spacing={4}
        sx={{
          pl: 16,
          m: 0,
        }}
      >
        {nodeProblems.map((nodeProblem) => (
          <Typography variant="P12R" component="li" key={nodeProblem}>
            {nodeProblem}
          </Typography>
        ))}
      </Stack>
    );
  }

  return <Typography variant="P12R">{nodeProblems[0]}</Typography>;
};

type ViewWorkloadsButtonProps = {
  node: NodeWithDependencies;
  shouldOpenWorkloadsInNewTab?: boolean;
};

const ViewWorkloadsButton = ({
  node,
  shouldOpenWorkloadsInNewTab = true,
}: ViewWorkloadsButtonProps) => {
  const { cluster } = useCluster();

  return (
    <Button
      data-testid="node-problems-alert-view-workloads-button"
      to={makeNodeDetailsLink({
        clusterId: cluster.id,
        nodeId: node.id,
        tab: 'workloads',
        filters: {
          binPackingReady: 'not-ready',
        },
      })}
      component={Link}
      variant="text"
      size="md"
      target={shouldOpenWorkloadsInNewTab ? '_blank' : undefined}
      endIcon={<Icons.ArrowRight />}
    >
      View workloads
    </Button>
  );
};

type InvestigateOpsPilotButtonProps = {
  onInvestigateOpsPilot: () => void;
  isOpsPilotEnabled: boolean;
};

/** Matches Automation Overview alert CTA for bin-packing blocked. */
const InvestigateOpsPilotButton = ({
  onInvestigateOpsPilot,
  isOpsPilotEnabled,
}: InvestigateOpsPilotButtonProps) => {
  const button = (
    <Button
      type="button"
      testId="node-problems-alert-investigate-castpilot-assistant"
      disabled={!isOpsPilotEnabled}
      onClick={() => {
        if (isOpsPilotEnabled) {
          onInvestigateOpsPilot();
        }
      }}
    >
      Investigate with {CAST_PILOT_ASSISTANT}
    </Button>
  );

  return (
    <Tooltip
      placement="left"
      title={`${CAST_PILOT_ASSISTANT} is not enabled for your organization`}
      wrapIf={!isOpsPilotEnabled}
    >
      {/* Disabled buttons ignore pointer events; span keeps the tooltip hoverable. */}
      {!isOpsPilotEnabled ? <span>{button}</span> : button}
    </Tooltip>
  );
};

type AlertContentProps = {
  binPackingBlockedDetails: BinPackingBlockedDetails;
  node: NodeWithDependencies;
  shouldOpenWorkloadsInNewTab?: boolean;
};

const AlertContent = ({
  binPackingBlockedDetails,
  node,
  shouldOpenWorkloadsInNewTab,
}: AlertContentProps) => {
  const viewWorkloadsButton = (
    <ViewWorkloadsButton
      node={node}
      shouldOpenWorkloadsInNewTab={shouldOpenWorkloadsInNewTab}
    />
  );

  switch (binPackingBlockedDetails.reason) {
    case 'node-problems':
      return (
        <NodeProblems nodeProblems={binPackingBlockedDetails.nodeProblems} />
      );
    case 'workloads-problems':
      return (
        <Stack gap={8}>
          <Typography>
            Some workloads on this node can’t be moved to other nodes. This
            blocks binpacking and downscaling.
          </Typography>
          {viewWorkloadsButton}
        </Stack>
      );
    case 'node-and-workloads-problems':
      return (
        <Stack gap={8}>
          <NodeProblems nodeProblems={binPackingBlockedDetails.nodeProblems} />
          {viewWorkloadsButton}
        </Stack>
      );
    default:
      return notReachable(binPackingBlockedDetails);
  }
};

type Props = {
  node: NodeWithDependencies;
  shouldOpenWorkloadsInNewTab?: boolean;
  /** When set, shows the same OpsPilot investigate CTA as cluster overview alerts. */
  onInvestigateOpsPilot?: () => void;
  isOpsPilotEnabled?: boolean;
};

export const NodeProblemsAlert = ({
  node,
  shouldOpenWorkloadsInNewTab,
  onInvestigateOpsPilot,
  isOpsPilotEnabled = true,
}: Props) => {
  const result = useMemo(() => checkBinPackingBlock(node), [node]);

  if (!result.isBinPackingBlocked) {
    return null;
  }

  const opsPilotAction = onInvestigateOpsPilot ? (
    <InvestigateOpsPilotButton
      onInvestigateOpsPilot={onInvestigateOpsPilot}
      isOpsPilotEnabled={isOpsPilotEnabled}
    />
  ) : undefined;

  return (
    <Alert
      testId="node-problems-alert"
      noIcon
      size="sm"
      color="warning"
      title="Bin packing blocked"
      action={opsPilotAction}
    >
      <AlertContent
        binPackingBlockedDetails={result.details}
        node={node}
        shouldOpenWorkloadsInNewTab={shouldOpenWorkloadsInNewTab}
      />
    </Alert>
  );
};
